import { useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import { Loader2, BarChart3 } from "lucide-react";

import CustomSelectWithFilter from "../../components/Common/CustomSelect/CustomSelectWithFilter";
import CustomMultiSelect from "../../components/Common/CustomSelect/CustomMultiSelect";
import {
    getUnitWiseCostChartData,
    getUnitWiseCostChartOptions,
    getUnitWiseCostChartHeight,
    unitWiseCostValueLabelPlugin,
} from "./chartConfigs/unitWiseCostChart";
import {
    COST_TYPE_OPTIONS,
    VALUE_MODE_OPTIONS,
    getChartFilterSummary,
} from "./unitWiseCostFilterOptions";
import { useUnitWiseCostData, makeProjectKey } from "./useUnitWiseCostData";
import ChartFilterBadges from "./ChartFilterBadges";

/**
 * Fully independent bar chart widget: owns its own FY / cost-type /
 * value-mode / project filters and fetches its own data. Changing filters
 * here has zero effect on any other chart instance.
 *
 * @param {object[]} masterProjects - shared project list (fetched once by parent)
 */
const UnitWiseCostBarChart = ({ masterProjects = [] }) => {

    const [selectedFY, setSelectedFY] = useState("OVERALL");
    const [costLevel, setCostLevel] = useState("TOTAL_COST");
    const [valueMode, setValueMode] = useState("PERCENT");
    const [selectedProjectKeys, setSelectedProjectKeys] = useState([]);

    const projectKeyToProject = useMemo(() => {

        const map = new Map();

        masterProjects.forEach((project) => {
            map.set(makeProjectKey(project.IO_NO, project.REV_NO), project);
        });

        return map;

    }, [masterProjects]);

    const {
        loading,
        sortedUnitData,
        financialYearOptions,
    } = useUnitWiseCostData({
        costLevel,
        valueMode,
        selectedFY,
        selectedProjectKeys,
        projectKeyToProject,
    });

    const chartData = useMemo(
        () => getUnitWiseCostChartData(sortedUnitData),
        [sortedUnitData]
    );

    const chartOptions = useMemo(
        () => getUnitWiseCostChartOptions(valueMode),
        [valueMode]
    );

    const chartHeight = getUnitWiseCostChartHeight(sortedUnitData.length);

    const filterSummary = useMemo(
        () => getChartFilterSummary({
            costLevel,
            selectedFY,
            valueMode,
            selectedProjectKeys,
            projectKeyToProject,
        }),
        [costLevel, selectedFY, valueMode, selectedProjectKeys, projectKeyToProject]
    );

    return (

        <div className="bg-white border border-[#EAECF0] rounded-xl p-5">

            {/* FILTERS */}

            <div
                className="
                    flex
                    flex-wrap
                    items-start
                    gap-4
                    p-4
                    mb-6
                    bg-[#F9FAFB]
                    border
                    border-[#EAECF0]
                    rounded-xl
                "
            >

                <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-[#667085]">Financial Year</span>
                    <CustomSelectWithFilter
                        data={financialYearOptions}
                        value={selectedFY}
                        onChange={(value) => setSelectedFY(value || "OVERALL")}
                        placeholder="Overall"
                        className="w-[200px]"
                        allowClear={false}
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-[#667085]">Cost Type</span>
                    <CustomSelectWithFilter
                        data={COST_TYPE_OPTIONS}
                        value={costLevel}
                        onChange={setCostLevel}
                        placeholder="Cost Type"
                        className="w-[200px]"
                        allowClear={false}
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-[#667085]">Value Mode</span>
                    <CustomSelectWithFilter
                        data={VALUE_MODE_OPTIONS}
                        value={valueMode}
                        onChange={setValueMode}
                        placeholder="Value Mode"
                        className="w-[200px]"
                        allowClear={false}
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-[#667085]">Projects Filter</span>
                    <CustomMultiSelect
                        data={masterProjects.map((project) => ({
                            label: project.PROJ_NAME,
                            value: makeProjectKey(project.IO_NO, project.REV_NO),
                        }))}
                        value={selectedProjectKeys}
                        onChange={setSelectedProjectKeys}
                        placeholder="All Projects"
                        className="w-[260px]"
                    />
                </div>

            </div>

            {/* HEADER */}

            <div className="flex items-center gap-2 mb-6">
                <BarChart3 size={20} className="text-[#155EEF]" />
                <div>
                    <div className="font-semibold text-[#101828]">
                        Unit Wise {filterSummary.costTypeLabel} Cost
                    </div>
                    <ChartFilterBadges
                        fyLabel={filterSummary.fyLabel}
                        valueMode={valueMode}
                        valueModeLabel={filterSummary.valueModeLabel}
                        projectNames={filterSummary.projectNames}
                    />
                </div>
            </div>

            {/* CONTENT */}

            {loading ? (
                <div className="flex items-center justify-center h-[400px]">
                    <Loader2 className="h-8 w-8 animate-spin text-[#155EEF]" />
                </div>
            ) : sortedUnitData.length === 0 ? (
                <div className="flex items-center justify-center h-[400px] text-sm text-[#667085]">
                    No data available
                </div>
            ) : (
                <div style={{ height: `${chartHeight}px` }}>
                    <Bar
                        data={chartData}
                        options={chartOptions}
                        plugins={[unitWiseCostValueLabelPlugin(valueMode)]}
                    />
                </div>
            )}

        </div>

    );

};

export default UnitWiseCostBarChart;